<script setup lang="ts">
import { useTemplateRef, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import ChevronDownIcon from '@/components/icons/IconChevronDown.vue';
import ChevronUpIcon from '@/components/icons/IconChevronUp.vue';
import Dropdown from '@/components/shared/base/Dropdown.vue';
import { useCountryStore } from '@/stores/country';
import { Country } from '@/types/components';

const selectedCountryCode = defineModel<string>({
  required: true,
  default: '',
});

const countryStore = useCountryStore();

const { t } = useI18n();

const buttonRef = useTemplateRef('buttonRef');
const isVisible = ref<boolean>(false);

const selectedCountry = ref<Country>({
  code: '',
  name: '',
});

const selectCountryCode = (country: Country) => {
  selectedCountryCode.value = country.code;
  handleDropdown(false);
};

const handleDropdown = (isActive: boolean) => {
  isVisible.value = isActive;
};

watch(
  () => {
    if (selectedCountryCode.value) {
      const country = countryStore.countries.find(({ code }) => selectedCountryCode.value === code);
      selectedCountry.value = country ?? { code: '', name: '' };
    }
  },
  { immediate: true }
);
</script>

<template>
  <div ref="dropdownContainerRef" :class="{ active: isVisible }" :data-off="!isVisible" class="dropdown">
    <button class="button-dim bk-line-btn" ref="buttonRef" @click="handleDropdown(true)" :class="{ active: isVisible }">
      <p>
        {{ selectedCountry.name || t('alert_select_country') }}
      </p>
      <component :is="isVisible ? ChevronUpIcon : ChevronDownIcon" />
    </button>
    <Dropdown v-if="isVisible" :trigger-ref="buttonRef" :class="{ active: isVisible }" @close="handleDropdown(false)">
      <ul v-if="countryStore.countries.length > 0" class="dropdown-content">
        <li v-for="country in countryStore.countries" :key="country.code" class="dropdown-item">
          <button
            class="m-btn button-white button-full-width"
            type="button"
            @click.prevent="selectCountryCode(country)"
          >
            {{ country.name }}
          </button>
        </li>
      </ul>
    </Dropdown>
  </div>
</template>

<style lang="scss" scoped>
.dropdown-trigger {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
  border-radius: 8px;
  cursor: pointer;

  &:hover,
  &:active,
  &.active {
    background-color: var(--color-gray-10);
  }
}

.bk-line-btn {
  border: 1px solid $color-gray-900;
  background: #fff;
  height: 48px;
  padding: 0 0.75rem;
  width: 100%;
  display: flex;
  justify-content: space-between;
}
</style>
